import React, { useEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { AnimationBlockFlowerPointer } from '../animations/AnimationBlockFlowerPointer';
import { useEventsByLocation, useTimeRange } from './utils/DayHooks';
import DayTimeline from './DayTimeline';
import LocationRow from './LocationRow';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/all';
import { DateUtils } from '@client/core/utils/DateUtils';
import { PixelsPerHour } from './utils/PixelsPerHour';

const Container = styled.div<{ $hours: number }>`
	position: relative;
	display: flex;
	flex-direction: column;
	min-width: 0;
	flex: 0 0 auto;
	${props => PixelsPerHour(pph => `width: calc(${props.$hours * pph}px + var(--gridMargin));`)}
	border: 0 solid currentColor;
	border-left-width: 1px;
	border-bottom-width: 1px;

	@supports (grid-template-rows: subgrid) {
		display: grid;
		grid-row: 1 / -1;
		grid-template-columns: auto;
		grid-template-rows: subgrid;
	}
`;

const IllustrationContainer = styled.div`
	grid-row: -1;
	display: flex;
	justify-content: flex-end;
	width: 100%;
	flex-direction: column;
`;

export default function SpilleplanDay({
	events,
	onEnterViewport,
	pointerIllustration = false,
}: {
	events: any[];
	onEnterViewport: () => void;
	pointerIllustration?: boolean;
}) {
	const containerRef = useRef<HTMLDivElement>(null);
	const timeRange = useTimeRange(events);

	const eventsByLocation = useEventsByLocation(events);

	useEffect(() => {
		gsap.registerPlugin(ScrollTrigger);

		const trigger = ScrollTrigger.create({
			trigger: containerRef.current,
			scroller: '#spilleplan',
			horizontal: true,
			start: 'left 33.33%',
			end: 'right 50%',
			onEnter: onEnterViewport,
			onEnterBack: onEnterViewport,
		});

		return () => {
			trigger?.kill();
		};
	}, [containerRef.current, onEnterViewport]);

	const hours = useMemo(() => {
		return Math.round(Math.max(DateUtils.getHoursBetween(timeRange.earliestTime, timeRange.latestTime), 3));
	}, [timeRange]);

	return (
		<Container className='ProgramDay' ref={containerRef} $hours={hours}>
			<DayTimeline from={timeRange.earliestTime} to={timeRange.latestTime} />
			{eventsByLocation.map((event, i) => (
				<LocationRow
					key={'event_' + i + '_' + event.location}
					index={i}
					events={event.events}
					location={event.location}
					hours={hours}
					dayStartTime={timeRange.earliestTime}
				/>
			))}
			{pointerIllustration && (
				<IllustrationContainer>
					<AnimationBlockFlowerPointer />
				</IllustrationContainer>
			)}
		</Container>
	);
}
